import React from "react";
import { Radio, RadioGroup } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { composeTailwindRenderProps } from "../../utils";

export interface RadioCardProps {
  /**
   * The value of the radio card
   */
  value: string;
  /**
   * Whether the radio card is disabled
   */
  isDisabled?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Children content
   */
  children: React.ReactNode;
  /**
   * Size variant
   */
  size?: "1" | "2" | "3";
  /**
   * Visual variant
   */
  variant?: "default" | "brand";
}

export interface RadioCardGroupProps {
  /**
   * The selected value
   */
  value?: string;
  /**
   * Default selected value
   */
  defaultValue?: string;
  /**
   * Callback when selection changes
   */
  onChange?: (value: string) => void;
  /**
   * Whether the radio group is disabled
   */
  isDisabled?: boolean;
  /**
   * Whether the radio group is read-only
   */
  isReadOnly?: boolean;
  /**
   * Whether the radio group is required
   */
  isRequired?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Children content
   */
  children: React.ReactNode;
  /**
   * Size variant
   */
  size?: "1" | "2" | "3";
  /**
   * Visual variant
   */
  variant?: "default" | "brand";
  /**
   * Grid columns configuration
   */
  columns?: string;
  /**
   * Gap between items
   */
  gap?: string;
}

function getSizeStyles(size: "1" | "2" | "3" = "2") {
  const sizeMap = {
    "1": "p-2 text-xs",
    "2": "p-3 text-sm",
    "3": "p-4 text-base",
  };
  return sizeMap[size];
}

function getVariantStyles(variant: "default" | "brand" = "default", isSelected: boolean) {
  if (!isSelected) {
    return "border-border text-text-body";
  }

  const variantMap = {
    default: "border-action-default text-action-default",
    brand: "border-action-brand text-action-brand",
  };
  return variantMap[variant];
}

function getDisabledStyles() {
  return "opacity-50 cursor-not-allowed";
}

export function RadioCard({ value, isDisabled, className, children, size = "2", variant = "default" }: RadioCardProps) {
  const focusRingColor = variant === "brand" ? "focus:ring-action-brand" : "focus:ring-action-default";

  return (
    <Radio
      value={value}
      isDisabled={isDisabled}
      className={composeTailwindRenderProps(
        className,
        `flex items-center justify-center cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 ${focusRingColor} focus:ring-offset-2 rounded-lg min-h-[44px]`
      )}
    >
      {({ isSelected, isDisabled: itemDisabled }) => (
        <div
          className={twMerge(
            "flex items-center justify-center w-full h-full transition-all duration-200 rounded-lg border-2 bg-background-surface",
            // Hover state (only when not disabled and not selected)
            !(isDisabled || itemDisabled) && !isSelected && "hover:bg-background-hover hover:border-border-hover",
            getSizeStyles(size),
            getVariantStyles(variant, isSelected),
            (isDisabled || itemDisabled) && getDisabledStyles(),
            className
          )}
        >
          {children}
        </div>
      )}
    </Radio>
  );
}

export function RadioCardGroup({
  value,
  defaultValue,
  onChange,
  isDisabled,
  isReadOnly,
  isRequired,
  className,
  children,
  size = "2",
  variant = "default",
  columns = "repeat(auto-fit, minmax(160px, 1fr))",
  gap = "1",
}: RadioCardGroupProps) {
  return (
    <RadioGroup
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      className={composeTailwindRenderProps(className, "grid")}
      style={{
        gridTemplateColumns: /^\d+$/.test(String(columns)) ? `repeat(${columns}, minmax(0, 1fr))` : columns,
        gap:
          gap === "1"
            ? "0.25rem"
            : gap === "2"
              ? "0.5rem"
              : gap === "3"
                ? "0.75rem"
                : gap === "4"
                  ? "1rem"
                  : typeof gap === "number"
                    ? `${gap}px`
                    : gap,
      }}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement<RadioCardProps>(child)) {
          return React.cloneElement(child, {
            size,
            variant,
          });
        }
        return child;
      })}
    </RadioGroup>
  );
}

// Export both components
export { RadioCard as Item, RadioCardGroup as Root };
